import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { usePermissionContext } from '../../context/PermissionContext';
import { getBrowserInfo } from '../../lib/browserInfo';
import { getRecentRequests } from '../../lib/networkBuffer';
import { getRecentErrors } from '../../lib/errorBuffer';
import { getRecentCorrelationIds } from '../../lib/correlation';

/**
 * Transparenz-Box (standardmäßig zugeklappt): zeigt dem Melder, welche Diagnosedaten
 * mit der Bug-Meldung übermittelt werden. Liest dieselben Quellen wie der Absende-Payload.
 */
export function DiagnosticsDisclosure({ hasScreenshot }: { hasScreenshot: boolean }) {
  const { t } = useTranslation();
  const { user, currentTenant } = useAuth();
  const { permissions, role, isSuperAdmin } = usePermissionContext();

  const browser = getBrowserInfo();
  const network = getRecentRequests(25);
  const errors = getRecentErrors(15);
  const correlationIds = getRecentCorrelationIds();

  const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <Box sx={{ display: 'flex', gap: 1, py: 0.25, fontSize: 13 }}>
      <Box sx={{ minWidth: 150, color: 'text.secondary', flexShrink: 0 }}>{label}</Box>
      <Box sx={{ wordBreak: 'break-word' }}>{children}</Box>
    </Box>
  );

  return (
    <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="body2" color="text.secondary">
          {t('bugReport.diagnosticsTitle')}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Row label={t('bugReport.diagUser')}>
          {user?.email ?? '-'} {role ? <Chip size="small" label={role} sx={{ ml: 0.5 }} /> : null}
        </Row>
        <Row label={t('bugReport.diagPermissions')}>
          {isSuperAdmin ? t('bugReport.diagSuperAdmin') : `${permissions.length}`}
        </Row>
        <Row label={t('bugReport.diagTenant')}>{currentTenant?.name ?? '-'}</Row>
        <Row label={t('bugReport.diagBrowser')}>
          {browser.name} {browser.version} ({browser.os})
          {!browser.supported && <Chip size="small" color="warning" label={t('bugReport.diagUnsupported')} sx={{ ml: 0.5 }} />}
        </Row>
        <Row label={t('bugReport.diagPage')}>{window.location.pathname}</Row>
        <Row label={t('bugReport.diagVersion')}>{__APP_VERSION__}</Row>
        <Row label={t('bugReport.diagWindow')}>
          {window.innerWidth}×{window.innerHeight}, {navigator.language}
        </Row>
        <Row label={t('bugReport.diagNetwork')}>{network.length}</Row>
        <Row label={t('bugReport.diagConsole')}>{errors.length}</Row>
        <Row label={t('bugReport.diagCorrelation')}>{correlationIds.length}</Row>
        <Row label={t('bugReport.diagScreenshot')}>{hasScreenshot ? t('bugReport.diagYes') : t('bugReport.diagNo')}</Row>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {t('bugReport.diagHint')}
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
}
